import React from "react";
import { format } from "date-fns";
import { Locale } from "date-fns";

interface StatementCustomerHeaderProps {
  companyLogo?: string;
  companyName: string;
  companyAddress?: string;
  companyCityStateZip?: string;
  companyPhone?: string;
  companyEmail?: string;
  customerData: any;
  customerEmail?: string;
  customerPhone?: string;
  dateLocale?: Locale;
  tr: (key: string) => string;
}

export default function StatementCustomerHeader({
  companyLogo,
  companyName,
  companyAddress,
  companyCityStateZip,
  companyPhone,
  companyEmail,
  customerData,
  customerEmail,
  customerPhone,
  dateLocale,
  tr,
}: StatementCustomerHeaderProps) {
  const getBillingAddress = () => {
    const billingAddress =
      customerData?.default_billing_address || customerData?.addresses?.[0];

    if (!billingAddress) return null;

    const addressLine = [
      billingAddress.address,
      billingAddress.city,
    ]
      .filter(Boolean)
      .join(", ");

    const zipCode = [
      billingAddress.zipcode,
      billingAddress.zip_ext,
    ]
      .filter(Boolean)
      .join("-");

    const stateLine = [
      billingAddress.state,
      zipCode,
    ]
      .filter(Boolean)
      .join(", ");

    return {
      addressLine,
      stateLine,
    };
  };
  const billingAddress = getBillingAddress();
  const customerName =  [customerData?.first_name, customerData?.last_name].filter(Boolean).join(" ")
  
  return (
    <div className="border-b border-slate-200 pb-6 space-y-4">
      {/* Top: Company info on left, Statement title & date on right */}
      <div className="flex justify-between items-start flex-wrap gap-6">
        <div>
          {companyLogo && (
            <img
              src={companyLogo}
              alt="Company Logo"
              className="h-12 max-h-12 w-auto object-contain mb-2 print:h-10"
            />
          )}
          <h2 className="text-xl font-bold text-slate-900 m-0 leading-tight">
            {companyName}
          </h2>
          {companyAddress && (
            <p className="text-xs text-slate-600 m-0 mt-0.5">{companyAddress}</p>
          )}
          {companyCityStateZip && (
            <p className="text-xs text-slate-600 m-0">{companyCityStateZip}</p>
          )}
          {companyPhone && (
            <p className="text-xs text-slate-600 m-0">{companyPhone}</p>
          )}
          {companyEmail && (
            <p className="text-xs text-slate-600 m-0">{companyEmail}</p>
          )}
        </div>

        <div className="text-right">
          <h1 className="text-2xl font-bold text-slate-900 m-0 tracking-wide">
            {tr("STATEMENT")}
          </h1>
          <p className="text-xs text-slate-500 mt-1 mb-0">
            {tr("Date")}:{" "}
            <span className="font-semibold text-slate-800">
              {format(new Date(), "MMMM d, yyyy", dateLocale ? { locale: dateLocale } : undefined)}
            </span>
          </p>
        </div>
      </div>

      {/* Bottom: Bill To / Customer Information */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3.5 print:bg-slate-50/50">
        <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
          {tr("Bill To")}:
        </p>
        <h3 className="text-sm font-bold text-slate-900 m-0">
          {customerData?.company_name || customerName || tr("Customer Details")}
        </h3>
        {billingAddress && (
          <div>
            {billingAddress.addressLine && (
              <p className="text-xs text-slate-600 m-0 mt-0.5">
                {billingAddress.addressLine}
              </p>
            )}

            {billingAddress.stateLine && (
              <p className="text-xs text-slate-600 m-0 mt-0.5">
                {billingAddress.stateLine}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
